import React, { useEffect, useState } from 'react'
import { Layout, notification, Button, Typography, Space, Card } from 'antd'
import {
  CalendarOutlined,
  ClockCircleOutlined,
  ThunderboltOutlined,
  VideoCameraOutlined,
  PhoneOutlined,
  MessageOutlined
} from '@ant-design/icons'
import SearchRefinementMobile from './SearchRefinementMobile'
import SearchResultsMobile from './SearchResultsMobile'
import DoctorAvailabilityViewMobile from './DoctorAvailabilityViewMobile'
import { useSearchRedux, useBookingRedux } from '../../hooks/reduxHooks'
import './mobile-styles.css'

const { Content } = Layout
const { Title, Text } = Typography

// Mobile search engine component
const MobileSearchEngine = ({ isVirtualVisit = false, preSelectedServiceType = null, onBack }) => {
  // Mobile navigation state
  const [currentScreen, setCurrentScreen] = useState('refinement') // 'refinement', 'results', 'availability'
  const [hasSearched, setHasSearched] = useState(false)
  const [showIntro, setShowIntro] = useState(() => {
    // Show intro only for virtual visit and if not seen this session
    try {
      const seen = sessionStorage.getItem('vc_intro_seen') === 'true'
      return isVirtualVisit && !seen
    } catch (_) {
      return isVirtualVisit
    }
  })

  // Redux hooks
  const {
    searchFilters,
    searchResults,
    loading,
    error,
    pagination,
    performSearch,
    changePage,
    updateFilter,
    clearError
  } = useSearchRedux()

  const searchType = searchFilters.searchType || 'general_practitioner'

  const bookingRedux = useBookingRedux()
  const {
    selectedDoctor,
    selectedSlot, // Used for pre-selected slot from quick booking
    showAvailabilityView,
    openAvailabilityView,
    closeAvailabilityView,
    bookingSuccess,
    bookingError,
    clearBookingError
  } = bookingRedux

  // Handle search with error handling
  const handleSearch = async (newFilters) => {
    try {
      setHasSearched(true)

      // For flexi/priority consultations, skip doctor search and go directly to booking
      if (newFilters.isFlexiPriority) {
        console.log('🚀 Flexi/Priority detected - skipping search, going to booking')

        // Create a virtual doctor for flexi/priority bookings
        const virtualDoctor = {
          _id: 'flexi-priority-virtual-doctor',
          firstName: 'Available',
          lastName: 'Doctor',
          extraData: {
            profession: 'Medical Practitioner'
          },
          clinicRecord: {
            _id: 'virtual-clinic-flexi-priority',
            name: 'Virtual Consultation',
            address:
              newFilters.serviceType === 'priority'
                ? 'Doctor will call within a few minutes'
                : 'Doctor will contact you during the day',
            // Add virtual consultation cost to trigger payment flow
            virtualConsultationCost: newFilters.serviceType === 'priority' ? 101 : 10 // Demo amounts
          }
        }

        // Open availability view directly with the virtual doctor
        openAvailabilityView(virtualDoctor)
        setCurrentScreen('availability')
        return
      }

      setCurrentScreen('results') // Navigate to results screen immediately
      await performSearch(newFilters) // This will trigger loading state
    } catch (err) {
      notification.error({
        message: 'Search Failed',
        description: err.message || 'Unable to perform search. Please try again.',
        duration: 4
      })
      setCurrentScreen('refinement') // Go back to refinement on error
    }
  }

  // Handle filter changes
  const handleFilterChange = (filterType, value) => {
    updateFilter(filterType, value)
  }

  // Handle searchType changes
  const setSearchType = (newSearchType) => {
    updateFilter('searchType', newSearchType)
  }

  // Handle page changes with error handling
  const handlePageChange = async (page) => {
    try {
      await changePage(page)
    } catch (err) {
      notification.error({
        message: 'Page Navigation Failed',
        description: err.message || 'Unable to load page. Please try again.',
        duration: 4
      })
    }
  }

  // Handle showing availability view
  const handleViewAvailability = (doctor) => {
    openAvailabilityView(doctor)
    setCurrentScreen('availability')
  }

  // Handle closing availability view
  const handleCloseAvailability = () => {
    closeAvailabilityView()
    setCurrentScreen('refinement')
  }

  // Handle going back to refinement
  const handleBackToRefinement = () => {
    setCurrentScreen('refinement')
  }

  // Handle going back to results
  const handleBackToResults = () => {
    setCurrentScreen('results')
  }

  // Scroll to top when screen changes
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }, [currentScreen])

  // Handle appointment booking success
  const handleBookAppointment = (appointmentData) => {
    // The success notification is now shown in the success screen
    // Just navigate back to refinement screen
    closeAvailabilityView()
    setCurrentScreen('refinement') // Navigate back to refinement screen
  }

  // Handle errors
  useEffect(() => {
    if (error) {
      notification.error({
        message: 'Error',
        description: error,
        duration: 4,
        onClose: clearError
      })
    }
  }, [error, clearError])

  useEffect(() => {
    if (bookingError) {
      notification.error({
        message: 'Booking Error',
        description: bookingError,
        duration: 4,
        onClose: clearBookingError
      })
    }
  }, [bookingError, clearBookingError])

  // Show booking success
  useEffect(() => {
    if (bookingSuccess) {
      notification.success({
        message: 'Booking Confirmed',
        description: 'Your appointment has been successfully booked!',
        duration: 4
      })
    }
  }, [bookingSuccess])

  // Initial search on component mount - skip for virtual visits
  useEffect(() => {
    if (showIntro) return
    if (searchResults.length === 0 && !loading && !isVirtualVisit) {
      performSearch(searchFilters)
    }
  }, [])

  // Hide intro handler
  const handleIntroContinue = () => {
    try {
      sessionStorage.setItem('vc_intro_seen', 'true')
    } catch (_) {}
    setShowIntro(false)
    // ensure we start at the top when entering the engine
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  // Intro overlay (shown before engine loads)
  if (showIntro) {
    return (
      <Layout style={{ minHeight: '100vh', background: 'linear-gradient(180deg, #f0f9ff 0%, #ffffff 60%)' }}>
        <Content style={{ padding: '24px 16px' }}>
          <div style={{ maxWidth: 520, margin: '0 auto' }}>
            <div style={{ textAlign: 'center', marginBottom: 24 }}>
              <div style={{ position: 'relative', display: 'inline-block', marginBottom: 12 }}>
                <div
                  style={{
                    width: 72,
                    height: 72,
                    borderRadius: '50%',
                    border: '3px solid rgba(59,130,246,0.2)',
                    borderTopColor: '#3b82f6',
                    animation: 'spin 1s linear infinite'
                  }}
                />
                <div
                  style={{
                    position: 'absolute',
                    inset: 8,
                    borderRadius: '50%',
                    background: 'linear-gradient(135deg,#3b82f6 0%, #06b6d4 100%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: '0 8px 20px rgba(59,130,246,0.25)'
                  }}
                >
                  <VideoCameraOutlined style={{ color: 'white', fontSize: 24 }} />
                </div>
              </div>
              <Title level={3} style={{ margin: 0 }}>
                How virtual consults work
              </Title>
              <Text style={{ color: '#6b7280' }}>A quick guide before you start</Text>
            </div>

            <Space direction='vertical' size={16} style={{ width: '100%' }}>
              <Card style={{ borderRadius: 12 }}>
                <Space align='start' size={12}>
                  <CalendarOutlined style={{ color: '#3b82f6', fontSize: 18 }} />
                  <div>
                    <Text strong>Choose your consult type</Text>
                    <div style={{ color: '#6b7280' }}>
                      Scheduled with a timeslot, or Flexi (today) / Priority (ASAP)
                    </div>
                  </div>
                </Space>
              </Card>

              <Card style={{ borderRadius: 12 }}>
                <Space align='start' size={12}>
                  <VideoCameraOutlined style={{ color: '#06b6d4', fontSize: 18 }} />
                  <div>
                    <Text strong>Pick your contact method</Text>
                    <div style={{ color: '#6b7280' }}>
                      In‑app video, phone call <PhoneOutlined /> or message/video <MessageOutlined />
                    </div>
                  </div>
                </Space>
              </Card>

              <Card style={{ borderRadius: 12 }}>
                <Space align='start' size={12}>
                  <ThunderboltOutlined style={{ color: '#f59e0b', fontSize: 18 }} />
                  <div>
                    <Text strong>Fast outcomes</Text>
                    <div style={{ color: '#6b7280' }}>
                      Priority calls you in minutes. Flexi reaches you during the day.
                    </div>
                  </div>
                </Space>
              </Card>

              <Card style={{ borderRadius: 12 }}>
                <Space align='start' size={12}>
                  <ClockCircleOutlined style={{ color: '#10b981', fontSize: 18 }} />
                  <div>
                    <Text strong>Optional Dis‑Chem script service</Text>
                    <div style={{ color: '#6b7280' }}>Choose delivery or collection to speed up your treatment.</div>
                  </div>
                </Space>
              </Card>

              <Button
                type='primary'
                size='large'
                onClick={handleIntroContinue}
                style={{ height: 48, border: 'none', background: 'linear-gradient(135deg,#3b82f6 0%, #06b6d4 100%)' }}
              >
                Got it, let’s begin
              </Button>
            </Space>
          </div>
        </Content>
      </Layout>
    )
  }

  // Show availability view full screen
  if (currentScreen === 'availability' && selectedDoctor) {
    return (
      <DoctorAvailabilityViewMobile
        doctor={selectedDoctor}
        onClose={handleCloseAvailability}
        onBookAppointment={handleBookAppointment}
        isVirtualVisit={isVirtualVisit}
        searchFilters={searchFilters}
        preSelectedSlot={bookingRedux.selectedSlot}
      />
    )
  }

  // Show refinement screen first
  if (currentScreen === 'refinement') {
    return (
      <Layout style={{ minHeight: '100vh', background: '#f9fafb' }}>
        <Content>
          <SearchRefinementMobile
            searchType={searchType}
            setSearchType={setSearchType}
            onSearch={handleSearch}
            onFilterChange={handleFilterChange}
            searchFilters={searchFilters}
            loading={loading}
            isFullScreen={true}
            hasSearched={hasSearched}
            isVirtualVisit={isVirtualVisit}
            preSelectedServiceType={preSelectedServiceType}
            onBack={onBack}
          />
        </Content>
      </Layout>
    )
  }

  // Show results screen
  if (currentScreen === 'results') {
    return (
      <Layout style={{ minHeight: '100vh', background: '#f9fafb' }}>
        <Content style={{ position: 'relative' }}>
          <SearchResultsMobile
            searchType={searchType}
            results={searchResults}
            loading={loading}
            onViewAvailability={handleViewAvailability}
            onBackToRefinement={handleBackToRefinement}
            error={error}
            totalResults={pagination.totalResults}
            searchFilters={searchFilters}
            currentPage={pagination.currentPage}
            totalPages={pagination.totalPages}
            onPageChange={handlePageChange}
          />
        </Content>
      </Layout>
    )
  }

  return null
}

export default MobileSearchEngine
